import clsx from 'clsx';
import { truncateText } from '@/utils/truncateText';
import { useState } from 'react';
import type { AgentApiCandidateEndpointsJobPostEndpointsRecord } from '@/types/serializers';
import { Button } from '../../../../components/Button';
import { OFFER_STATUS_ITEMS } from '../../candidates/constants';
import CandidatesStatusChangeModal from '../../candidates/components/CandidatesStatusChangeModal';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryObserverResult } from '@tanstack/react-query';
import type { CandidateEndpointResponse } from '../../candidates/types/CandidateEndpoint';
import CandidatesUserJobProcessScore from '../../candidates/components/CandidatesUserJobProcessScore';

const STATUS_COLORS = {
  suggest: 'bg-tag-orange text-tag-orange',
  apply: 'bg-tag-green text-tag-green',
  decline: 'bg-tag-gray',
  archived: 'bg-tag-red text-tag-red',
  matched: 'bg-tag-blue text-tag-blue',
} as const;

type StatusChangeType = 'toSuggest' | 'toSelection' | 'toWithdraw';

interface Props {
  endpoint: AgentApiCandidateEndpointsJobPostEndpointsRecord;
  companyRefetch?: () => void;
}

export default function JobPostEndpointCard({ endpoint, companyRefetch }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusChangeType, setStatusChangeType] = useState<StatusChangeType>('toSuggest');
  const queryClient = useQueryClient();

  const openModal = (type: StatusChangeType) => {
    setStatusChangeType(type);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const candidateEndpointRefetch = async (): Promise<
    QueryObserverResult<CandidateEndpointResponse, Error>
  > => {
    await queryClient.invalidateQueries({
      queryKey: ['/agent/api/candidate_endpoints'],
      exact: false,
    });

    if (companyRefetch) {
      companyRefetch();
    }

    return {} as QueryObserverResult<CandidateEndpointResponse, Error>;
  };

  return (
    <div className="flex min-h-[190px] flex-col gap-3 rounded bg-white p-8 shadow-layer1 transition-shadow hover:shadow-layer2">
      <div className="flex items-center justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <div className="flex flex-col gap-md">
            <h3 className="block truncate text-xl font-bold">{endpoint.candidate_profile?.name}</h3>
            <div className="flex items-start justify-between gap-1">
              <div className="flex flex-wrap gap-4">
                {endpoint.provided_job_post.company && (
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-bold text-placeholder">勤務会社</p>
                    <p className="text-xs">{endpoint.provided_job_post.company.name}</p>
                  </div>
                )}
                {endpoint.provided_job_post.job_position_name && (
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-bold text-placeholder">ポジション</p>
                    <p className="text-xs" title={endpoint.provided_job_post.job_position_name}>
                      {truncateText(endpoint.provided_job_post.job_position_name)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <CandidatesUserJobProcessScore
              text="提案"
              score={endpoint.provided_job_post.counter_cache.count_of_suggest || 0}
              size="small"
            />
            <CandidatesUserJobProcessScore
              text="書類選考"
              score={endpoint.provided_job_post.counter_cache.count_of_document_screening || 0}
              size="small"
            />
            <CandidatesUserJobProcessScore
              text="選考中"
              score={endpoint.provided_job_post.counter_cache.count_of_interview || 0}
              size="small"
            />
            <CandidatesUserJobProcessScore
              text="内定"
              score={endpoint.provided_job_post.counter_cache.count_of_offer || 0}
              size="small"
            />
            <CandidatesUserJobProcessScore
              text="内定承諾"
              score={endpoint.provided_job_post.counter_cache.count_of_close || 0}
              size="small"
            />
            <div className="mb-auto flex w-[94px] justify-end">
              <span
                className={clsx('rounded-full px-3 py-2 text-sm', STATUS_COLORS[endpoint.status])}
              >
                {OFFER_STATUS_ITEMS.find((item) => item.value === endpoint.status)?.label ||
                  endpoint.status}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2"></div>
      </div>

      <div className="mt-lg flex items-center justify-between">
        <Button color="secondary" asChild>
          <a href={endpoint.candidate_profile?.url} target="_blank" rel="noreferrer">
            求職者ページを開く
          </a>
        </Button>

        <div className="flex gap-2">
          {(endpoint.status === 'suggest' || endpoint.status === 'apply') && (
            <>
              <Button
                className="w-[120px]"
                color="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  openModal('toWithdraw');
                }}
              >
                取り下げ
              </Button>
              <Button
                className="w-[120px]"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  openModal('toSelection');
                }}
              >
                選考へ進む
              </Button>
            </>
          )}

          {(endpoint.status === 'decline' || endpoint.status === 'archived') && (
            <Button
              className="w-[120px]"
              onClick={(e) => {
                e.stopPropagation();
                openModal('toSuggest');
              }}
            >
              提案へ戻す
            </Button>
          )}
        </div>
      </div>

      <CandidatesStatusChangeModal
        modal={{
          id: `job-post-endpoint-status-change-modal-${endpoint.id}`,
          isShow: isModalOpen,
        }}
        close={closeModal}
        statusChangeType={statusChangeType}
        candidateProfileId={endpoint.candidate_profile?.id || 0}
        ids={[endpoint.id]}
        candidateEndpointRefetch={candidateEndpointRefetch}
      />
    </div>
  );
}
